<script>
    import { createEventDispatcher, onMount } from "svelte";

    import SongItem from "../components/SongItem.svelte";

    import { fetchMediaItems, getArtworkURL } from "../utils.js";

    export let panel = "player";

    const dispatch = createEventDispatcher();

    const maxUint8Value = 2 ** 8;
    const margin = 5;
    const minHeight = 20;

    let music = MusicKit.getInstance();

    let canvas;
    let audioCtx;
    let audioSource;
    let audioAnalyser;
    let audioBuffer;
    let canvasCtx;

    $: player = music;
    $: queue = music.queue;

    $: currentItem =
        music.queue.position != -1 &&
        music.queue.items[music.queue.position];

    $: cachedQueue = music.queue.items;

    $: current =
        music.queue.position != -1 &&
        cachedQueue[music.queue.position];
    $: attrs = current && current.attributes;
    $: explicit = attrs && attrs.contentRating == "explicit";

    $: artworkUrl =
        (attrs && getArtworkURL(current, 80, 80)) || "/missing-artwork.svg";

    // $: console.log("player item:", current);
    // $: console.log("player attrs:", attrs);
    // $: console.log("player artwork URL:", artworkUrl);

    $: queueSections = (() => {
        const previousItems = cachedQueue
            .slice(0, music.queue.position)
            .map((item, position) => ({ item, position }));
        const currentItems = [
            {
                item: cachedQueue[music.queue.position],
                position: music.queue.position,
            },
        ];
        const upNextItems = cachedQueue
            .slice(music.queue.position + 1)
            .map((item, idx) => ({
                item,
                position: music.queue.position + 1 + idx,
            }));
        return [
            { name: "Previous", items: previousItems },
            { name: "Current", items: currentItems },
            { name: "Up Next", items: upNextItems },
        ];
    })();

    $: playbackProgress = Math.round(player.currentPlaybackProgress * 100);

    $: playbackTime = player.currentPlaybackTime;
    $: remainingTime = player.currentPlaybackTimeRemaining;

    $: formattedPlaybackTime = MusicKit.formatMediaTime(playbackTime, ':');
    $: formattedRemainingTime = MusicKit.formatMediaTime(remainingTime, ':');

    $: player.volume = Math.min(player.volume, 0.5);
    $: volume = Math.round(player.volume * 100);
    
    $: playbackState = player.playbackState;

    if (navigator.mediaSession) {
        navigator.mediaSession.setActionHandler("play", () => {
            music.play();
        });
        navigator.mediaSession.setActionHandler("pause", () => {
            music.pause();
        });
        navigator.mediaSession.setActionHandler("seekbackward", () => {
            music.seekBackward();
        });
        navigator.mediaSession.setActionHandler("seekforward", () => {
            music.seekForward();
        });
        navigator.mediaSession.setActionHandler("previoustrack", () => {
            music.skipToPreviousItem();
        });
        navigator.mediaSession.setActionHandler("nexttrack", () => {
            music.skipToNextItem();
        });
        navigator.mediaSession.setPositionState()
    }

    $: if (navigator.mediaSession && current && attrs) {
        navigator.mediaSession.metadata = new MediaMetadata({
            title: attrs.name,
            artist: attrs.artistName,
            // album: attrs.albumName,
            artwork: [
                {
                    src: getArtworkURL(current, 96, 96),
                    sizes: "96x96",
                    type: "image/png",
                },
                {
                    src: getArtworkURL(current, 128, 128),
                    sizes: "128x128",
                    type: "image/png",
                },
                {
                    src: getArtworkURL(current, 192, 192),
                    sizes: "192x192",
                    type: "image/png",
                },
                {
                    src: getArtworkURL(current, 256, 256),
                    sizes: "256x256",
                    type: "image/png",
                },
                {
                    src: getArtworkURL(current, 384, 384),
                    sizes: "384x384",
                    type: "image/png",
                },
                {
                    src: getArtworkURL(current, 512, 512),
                    sizes: "512x512",
                    type: "image/png",
                },
            ],
        });
        // navigator.mediaSession.setPositionState({
        //     duration: music.currentPlaybackDuration,
        //     position: Math.min(playbackTime, music.currentPlaybackDuration),
        //     playbackRate: 1,
        // });
    }

    $: if (navigator.mediaSession) {
        switch (playbackState) {
            case MusicKit.PlaybackStates.playing:
                navigator.mediaSession.playbackState = "playing";
                break;
            case MusicKit.PlaybackStates.paused:
                navigator.mediaSession.playbackState = "paused";
                break;
            default:
                navigator.mediaSession.playbackState = "none";
                break;
        }
    }

    function sampleElements(arr, count) {
        const step = arr.length / count;
        const sampled = [];
        for (let idx = 0; idx < arr.length; idx += step) {
            sampled.push(arr[Math.floor(idx)]);
        }
        return sampled;
    }

    function sampleElementsAvg(arr, count) {
        const size = arr.length / count;
        const sampled = [];
        for (let idx = 0; idx < count; idx += 1) {
            const slice = arr.slice(idx * size, (idx + 1) * size);
            if (slice.length > 0) {
                sampled.push(
                    slice.reduce((acc, it) => acc + it, 0) / slice.length
                );
            } else {
                sampled.push(0);
            }
        }
        return sampled;
    }

    // initialize FFT on first use.
    let fftInitialized = false;

    function renderFFTFrame() {
        requestAnimationFrame(() => renderFFTFrame());

        if (panel != "fft") return;

        canvas.width = canvas.clientWidth * devicePixelRatio;
        canvas.height = canvas.clientHeight * devicePixelRatio;
        const width = canvas.width;
        const height = canvas.height;

        audioAnalyser.getByteFrequencyData(audioBuffer);
        window.audioAnalyser = audioAnalyser;
        window.audioBuffer = audioBuffer;

        const sampled = sampleElementsAvg([...audioBuffer], 16);
        const reversed = [...sampled];
        reversed.reverse();
        const data = [...reversed, ...sampled];
        const barWidth = (width - margin) / data.length - margin;

        const points = data.map((value, index) => ({
            x: index * (barWidth + margin) + margin,
            y: (value / maxUint8Value) * height,
        }));

        function roundRect({ x, y, w, h, r }) {
            if (w < 2 * r) r = w / 2;
            if (h < 2 * r) r = h / 2;
            canvasCtx.beginPath();
            canvasCtx.moveTo(x + r, y);
            canvasCtx.arcTo(x + w, y, x + w, y + h, r);
            canvasCtx.arcTo(x + w, y + h, x, y + h, r);
            canvasCtx.arcTo(x, y + h, x, y, r);
            canvasCtx.arcTo(x, y, x + w, y, r);
            canvasCtx.closePath();
            canvasCtx.fill();
        }

        canvasCtx.fillStyle = "#FFF";
        canvasCtx.lineWidth = 32;
        for (let idx = 0; idx < points.length; idx++) {
            const height = Math.max(points[idx].y, minHeight);
            roundRect({
                x: points[idx].x,
                y: canvas.height / 2 - height / 2,
                w: barWidth,
                h: height,
                r: barWidth,
            });
        }
    }

    function handleSeek(event) {
        const value = parseInt(event.target.value, 10) / 100;
        const item = music.nowPlayingItem;
        if (item == undefined) return;
        const {
            attributes: { durationInMillis },
        } = item;
        music.seekToTime(Math.round((durationInMillis / 1000) * value));
    }

    function handleLoopChange() {
        music.repeatMode = (music.repeatMode + 1) % 3;
    }

    function handleVolumeChange(event) {
        const value = parseInt(event.target.value, 10) / 100;
        player.volume = value;
    }

    function handleShuffleChange() {
        music.shuffleMode = (music.shuffleMode + 1) % 2;
    }

    function handleQueueItemClick(item, position) {
        return async function (event) {
            await music.changeToMediaAtIndex(position);
        };
    }
    
    function handleQueueItemContextMenu(item) {
        return function (event) {
            const { pageX, pageY, target } = event.detail;
            dispatch("context-menu", { item, pageX, pageY, target });
        };
    }

    function handleKeyDown(ev) {
        if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) return;
        const music = MusicKit.getInstance();
        switch (ev.key) {
            case " ":
                ev.preventDefault();
                if (playbackState == MusicKit.PlaybackStates.playing) {
                    music.pause();
                } else {
                    music.play();
                }
                return;
            case "r":
                ev.preventDefault();
                handleLoopChange();
                return;
            case "Down":
            case "ArrowDown":
                ev.preventDefault();
                music.volume = Math.max(music.volume - 0.05, 0);
                return;
            case "Up":
            case "ArrowUp":
                ev.preventDefault();
                music.volume = Math.min(music.volume + 0.05, 0.5);
                return;
            case "Left":
            case "ArrowLeft":
                ev.preventDefault();
                music.skipToPreviousItem();
                return;
            case "Right":
            case "ArrowRight":
                ev.preventDefault();
                music.skipToNextItem();
                return;
        }
    }

    let itemDragged = false;
    let itemDraggedOver = false;

    function handleDragEnter(event) {
        event.preventDefault();
        itemDraggedOver = true;
    }

    function handleDragOver(event) {
        event.preventDefault();
    }

    function handleDragLeave(event) {
        itemDraggedOver = false;
    }

    function handleDrop(event) {
        itemDragged = false;
        itemDraggedOver = false;
        const contents = event.dataTransfer.getData("application/json");
        console.log("item dropped", contents);
        if (contents == undefined) {
            return;
        }
        (async () => {
            const item = JSON.parse(contents);
            const items = await fetchMediaItems(item.type, item.id);
            await music.setQueue({ items });
            await music.changeToMediaAtIndex(0);
            await music.play();
        })();
    }

    let invalidateSoon = false;
    function invalidateAll() {
        music = MusicKit.getInstance();
        invalidateSoon = false;
    }

    function invalidate() {
        player = music;
    }

    onMount(() => {
        music.addEventListener("mediaItemDidChange", invalidateAll);
        music.addEventListener("queueItemsDidChange", () => {
            invalidateSoon = true;
            setTimeout(() => {
                if (invalidateSoon) {
                    invalidateAll();
                }
            }, 1000);
        });
        music.addEventListener("queuePositionDidChange", invalidateAll);
        music.addEventListener("playbackTimeDidChange", invalidate);
        music.addEventListener("playbackVolumeDidChange", invalidate);
        music.addEventListener("playbackStateDidChange", invalidate);

        function handleDragStart() {
            itemDragged = true;
        }
        function handleDragEnd() {
            itemDragged = false;
        }

        document.addEventListener("dragstart", handleDragStart);
        document.addEventListener("dragend", handleDragEnd);

        return function () {
            music.removeEventListener("mediaItemDidChange", invalidateAll);
            music.removeEventListener("queueItemsDidChange");
            music.removeEventListener("queuePositionDidChange", invalidateAll);
            music.removeEventListener("playbackTimeDidChange", invalidate);
            music.removeEventListener("playbackVolumeDidChange", invalidate);
            music.removeEventListener("playbackStateDidChange", invalidate);

            document.removeEventListener("dragstart", handleDragStart);
            document.removeEventListener("dragend", handleDragEnd);
        };
    });

    function handleFFTClick() {
        panel = "fft";
        if (fftInitialized == false && canvas) {
            fftInitialized = true;
            canvasCtx = canvas.getContext("2d");
            audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioAnalyser = audioCtx.createAnalyser();
            audioAnalyser.fftSize = 2048;
            audioAnalyser.smoothingTimeConstant = 0.7;
            audioSource = audioCtx.createMediaElementSource(
                document.querySelector("audio")
            );

            audioSource.connect(audioAnalyser);
            audioAnalyser.connect(audioCtx.destination);

            audioBuffer = new Uint8Array(audioAnalyser.frequencyBinCount);

            renderFFTFrame();
        }
    }

    // this.addEventListener("dragenter", ev => {
    //     if (ev.target == ev.currentTarget) {
    //         this.draggedOver = true;
    //         this.requestUpdate();
    //     }
    // });
    // this.addEventListener("dragleave", ev => {
    //     if (ev.target == ev.currentTarget) {
    //         this.draggedOver = false;
    //         this.requestUpdate();
    //     }
    // });
    // document.addEventListener("dragstart", ev => {
    //     this.dropZone = true;
    //     this.requestUpdate();
    // });
    // document.addEventListener("dragend", ev => {
    //     this.dropZone = false;
    //     this.requestUpdate();
    // });
    // this.addEventListener("dragover", ev => {
    //     ev.preventDefault();
    // });
    // this.addEventListener("drop", ev => {
    //     this.dropZone = false;
    //     this.draggedOver = false;
    //     this.requestUpdate();
    //     const contents = ev.dataTransfer.getData("application/json");
    //     if (contents == undefined)
    //         return;
    //     (async () => {
    //         const data = JSON.parse(contents);
    //         const { attributes: { playParams: { id, kind } } } = data;
    //         const music = MusicKit.getInstance();
    //         if (kind == "song") {
    //             await music.setQueue({ items: [data] });
    //         } else {
    //             await music.setQueue({ [kind]: id });
    //         }
    //         await music.changeToMediaAtIndex(0);
    //         await music.play();
    //         this.requestUpdate();
    //     })();
    // });
    // document.addEventListener("keydown", ev => {
    //     const { metaKey, altKey, shiftKey } = ev;
    //     if (metaKey || altKey || shiftKey)
    //         return;
    //     const music = MusicKit.getInstance();
    //     switch (ev.key) {
    //         case " ":
    //             ev.preventDefault();
    //             if (playbackState == MusicKit.PlaybackStates.playing) {
    //                 music.pause();
    //             } else {
    //                 music.play();
    //             }
    //             return;
    //         case "Down":
    //         case "ArrowDown":
    //             ev.preventDefault();
    //             music.volume = Math.max(music.volume - 0.05, 0);
    //             return;
    //         case "Up":
    //         case "ArrowUp":
    //             ev.preventDefault();
    //             music.volume = Math.min(music.volume + 0.05, 0.5);
    //             return;
    //         case "Left":
    //         case "ArrowLeft":
    //             ev.preventDefault();
    //             music.skipToPreviousItem();
    //             return;
    //         case "Right":
    //         case "ArrowRight":
    //             ev.preventDefault();
    //             music.skipToNextItem();
    //             return;
    //     }
    // });
</script>

{#if queue.isEmpty}
    <div
        class="message"
        on:dragenter={handleDragEnter}
        on:dragleave={handleDragLeave}
        on:dragover={handleDragOver}
        on:drop={handleDrop}
    >
        Empty queue.
        {#if itemDragged}
            <div class="drop-hint {itemDraggedOver ? 'drop-zone' : ''}" />
        {/if}
    </div>
{:else}
    <div
        class="root"
        on:dragenter={handleDragEnter}
        on:dragleave={handleDragLeave}
        on:dragover={handleDragOver}
        on:drop={handleDrop}
    >
        <div class="overlay" style="--background-url: url({artworkUrl})" />
        <div class="background" />
        <div class="player {panel == 'player' ? 'shown' : 'hidden'}">
            <div class="infos">
                <div class="art">
                    <img
                        src={artworkUrl}
                        alt="{attrs.artistName} - {attrs.name}"
                    />
                </div>
                <div class="metadata">
                    <div class="title" title={attrs.name}>{attrs.name}</div>
                    <div class="artist" title={attrs.artistName}>
                        {attrs.artistName}
                    </div>
                    <div class="album" title={attrs.albumName}>
                        {attrs.albumName}
                    </div>
                    {#if explicit}
                        <div class="explicit" title="Explicit song">
                            <span>E</span>
                        </div>
                    {/if}
                </div>
            </div>
            <div class="timeline-controls">
                <div class="timeline range">
                    <div class="duration">{formattedPlaybackTime}</div>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={playbackProgress}
                        on:change={handleSeek}
                        style="background: linear-gradient(
                            to right,
                            #FFF 0%,
                            #FFF {playbackProgress}%,
                            #333 {playbackProgress}%,
                            #333 100%
                        ) no-repeat"
                    />
                    <div class="duration">{formattedRemainingTime}</div>
                </div>
                <div class="controls-container">
                    <div
                        class="control-toggle last"
                        on:click={() => music.skipToPreviousItem()}
                    >
                        <span class="icon-backward" />
                    </div>
                    {#if playbackState == MusicKit.PlaybackStates.playing}
                        <div
                            class="control-toggle play-pause"
                            on:click={() => music.pause()}
                        >
                            <span class="icon-pause" />
                        </div>
                    {:else}
                        <div
                            class="control-toggle play-pause"
                            on:click={() => music.play()}
                        >
                            <span class="icon-play" />
                        </div>
                    {/if}
                    <div
                        class="control-toggle next"
                        on:click={() => music.skipToNextItem()}
                    >
                        <span class="icon-forward" />
                    </div>
                </div>
            </div>
            <div class="misc">
                <div class="control-toggle pop-out" on:click={handleFFTClick}>
                    <span class="icon-pop-out" />
                </div>
                <div
                    class="control-toggle loop"
                    class:toggled={music.repeatMode == 1}
                    class:toggled-alt={music.repeatMode == 2}
                    on:click={handleLoopChange}
                >
                    <span class="icon-loop" />
                </div>
                <div class="volume range">
                    <span class="icon-volume-medium" />
                    <input
                        type="range"
                        min="0"
                        max="50"
                        value={volume}
                        on:input={handleVolumeChange}
                        style="background: linear-gradient(
                            to right,
                            #FFF 0%,
                            #FFF {volume * 2}%,
                            #333 {volume * 2}%,
                            #333 100%
                        ) no-repeat"
                    />
                </div>
                <div
                    class="control-toggle shuffle"
                    class:toggled={music.shuffleMode != 0}
                    on:click={handleShuffleChange}
                >
                    <span class="icon-shuffle" />
                </div>
                <div
                    class="control-toggle list"
                    on:click={() => (panel = "queue")}
                >
                    <span class="icon-list" />
                </div>
            </div>
        </div>
        <div
            class="queue {panel == 'queue' ? 'shown' : 'hidden'}"
            on:click={() => (panel = "player")}
        >
            <div class="queue-contents">
                {#each queueSections as { name, items }}
                    {#if items.length}
                        <div class="queue-section">{name}:</div>
                        {#each items as { item, position }}
                            <SongItem
                                {item}
                                on:click={handleQueueItemClick(item, position)}
                                on:context-menu={handleQueueItemContextMenu(item.container)}
                            />
                        {/each}
                    {/if}
                {/each}
            </div>
        </div>
        <div
            class="fft {panel == 'fft' ? 'shown' : 'hidden'}"
            on:click={() => (panel = "player")}
        >
            <canvas
                class="fft-canvas"
                bind:this={canvas}
                width="300"
                height="225"
            />
        </div>
        {#if itemDragged}
            <div class="drop-hint {itemDraggedOver ? 'drop-zone' : ''}" />
        {/if}
    </div>
{/if}

<svelte:window on:keydown={handleKeyDown} />

<style>
    .root {
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
        color: #fff;
    }

    .overlay,
    .background {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
    }

    .overlay {
        background: var(--background-url) no-repeat center;
        background-size: cover;
        filter: saturate(300%) blur(10px);
    }

    .background {
        background-color: #0007;
    }

    .player {
        position: absolute;
        top: 0;
        left: 0;
        display: grid;
        grid-template-rows: min-content 1fr 1fr;
        grid-template-columns: 1fr;
        grid-template-areas: "infos" "timeline-controls" "misc";
        grid-gap: 5px;
        width: 100%;
        height: 100%;
        padding: 10px;
        box-sizing: border-box;
        color: #fff;
        opacity: 1;
        transform: none;
        transition: transform 0.4s, opacity 0.4s;
        will-change: transform, opacity;
    }

    .player.shown {
        opacity: 1;
        transform: none;
    }

    .player.hidden {
        opacity: 0;
        transform: scale(1.4);
        pointer-events: none;
    }

    .infos {
        grid-area: infos;
        width: 100%;
        height: 100%;
        display: grid;
        grid-gap: 10px;
        grid-template-areas: "art metadata";
        grid-template-columns: min-content 1fr;
        grid-template-rows: 1fr;
        overflow: hidden;
    }

    .art {
        grid-area: art;
    }

    .art > img {
        width: 80px;
        height: 80px;
        border-radius: 5px;
        object-fit: cover;
    }

    .metadata {
        grid-area: metadata;
        overflow: hidden;
    }

    .title {
        font-size: 18px;
        font-weight: bold;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-align: left;
    }

    .artist,
    .album {
        font-size: 15px;
        color: var(--app-dark-secondary-fg-color);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-align: left;
    }

    .explicit {
        text-align: left;
    }

    .explicit > span {
        font-size: 15px;
        font-weight: bold;
        color: var(--player-explicit-fg-color, var(--app-explicit-fg-color));
        background-color: var(
            --player-explicit-bg-color,
            var(--app-explicit-bg-color)
        );
        border-radius: 2px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-align: left;
        padding: 0 5px;
    }

    .timeline-controls {
        grid-area: timeline-controls;
        display: grid;
        grid-template-rows: min-content 1fr;
        grid-template-columns: 1fr;
        grid-gap: 10px;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    .controls-container {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: repeat(5, 1fr);
        grid-template-areas: ". last playpause next .";
        width: 100%;
    }

    .timeline {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .last {
        grid-area: last;
        font-size: 33px;
    }

    .play-pause {
        grid-area: playpause;
        font-size: 33px;
    }

    .next {
        grid-area: next;
        font-size: 33px;
    }

    .control-toggle {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        cursor: pointer;
        border-radius: 10px;
    }

    .control-toggle > span {
        transition: transform 0.2s;
        padding: 6px;
        border-radius: 5px;
        border: solid 2px;
        border-color: transparent;
    }

    .control-toggle:hover > span {
        transform: scale(1.3);
    }

    .control-toggle:active > span {
        transform: scale(0.7);
    }

    .control-toggle.toggled > span {
        color: #171717;
        background-color: #fff;
        border-color: #fff;
    }

    .control-toggle.toggled-alt > span {
        color: #fff;
        background-color: transparent;
        border-color: #fff;
    }

    .misc {
        grid-area: misc;
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: 1fr 1fr 3fr 1fr 1fr;
        width: 100%;
        height: 100%;
    }

    .volume {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: min-content 1fr;
        grid-gap: 10px;
        align-items: center;
        justify-content: center;
        text-align: center;
        width: 100%;
        height: 100%;
        padding: 10px;
        box-sizing: border-box;
    }

    .message {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        position: relative;
        width: 100%;
        height: 100%;
        font-size: 25px;
        font-weight: bold;
        background-color: var(--player-bg-color, #e0e0e0);
        color: var(--player-fg-color, #171717);
    }

    .timeline {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: minmax(4ch, min-content) 1fr minmax(
                4ch,
                min-content
            );
        grid-gap: 5px;
        width: 100%;
        height: 100%;
    }

    .timeline > input[type="range"],
    .volume > input[type="range"] {
        margin: 4px auto;
        width: 100%;
        height: 10px;
        outline: none;
        border-radius: 2px;
        background: linear-gradient(
                to right,
                #333 0%,
                #333 100%
            )
            no-repeat;
        cursor: pointer;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
    }

    .range > input[type="range"]::-webkit-slider-runnable-track {
        box-shadow: none;
        border: none;
        background: transparent;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
    }
    .range > input[type="range"]::-moz-range-track {
        box-shadow: none;
        border: none;
        background: transparent;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
    }
    .range > input[type="range"]::-ms-track {
        box-shadow: none;
        border: none;
        background: transparent;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
    }

    .range > input[type="range"]::-webkit-slider-thumb {
        height: 20px;
        width: 10px;
        border: none;
        background: #fff;
        border-radius: 2px;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
        transition: transform 0.2s;
    }
    .range > input[type="range"]::-moz-range-thumb {
        height: 20px;
        width: 10px;
        border: none;
        background: #fff;
        border-radius: 2px;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
        transition: transform 0.2s;
    }
    .range > input[type="range"]::-ms-thumb {
        height: 20px;
        width: 10px;
        border: none;
        background: #fff;
        border-radius: 2px;
        appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
        transition: transform 0.2s;
    }

    .range > input[type="range"]:hover::-webkit-slider-thumb {
        transform: scale(1.4);
    }
    .range > input[type="range"]:hover::-moz-range-thumb {
        transform: scale(1.4);
    }
    .range > input[type="range"]:hover::-ms-thumb {
        transform: scale(1.4);
    }

    .range > input[type="range"]:focus {
        outline: none;
    }

    .queue {
        display: grid;
        grid-gap: 5px;
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: auto;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        text-align: left;
        padding: 10px;
        box-sizing: border-box;
        transition: transform 0.4s, opacity 0.4s;
        will-change: transform, opacity;
    }

    .queue.shown {
        opacity: 1;
        transform: none;
    }

    .queue.hidden {
        opacity: 0;
        transform: scale(0.6);
        pointer-events: none;
    }

    .queue-contents {
        display: flex;
        flex-direction: column;

        --song-item-padding: 5px;
        --song-item-fg-color: #FFF;
        --song-item-hover-fg-color: var(--app-light-fg-color);
        --song-item-hover-bg-color: #FFF;
        --song-item-secondary-fg-color: var(--app-dark-secondary-fg-color);
        --song-item-secondary-bg-color: var(--app-dark-secondary-bg-color);
        --song-item-hover-secondary-fg-color: var(--app-light-secondary-fg-color);
        --song-item-hover-secondary-bg-color: var(--app-light-secondary-bg-color);
    }

    .queue-section {
        font-size: 20px;
        font-weight: bold;
        padding: 3px 0;
    }

    .fft {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        overflow: hidden;
        transition: transform 0.4s, opacity 0.4s;
        will-change: transform, opacity;
    }

    .fft.shown {
        opacity: 1;
        transform: none;
    }

    .fft.hidden {
        opacity: 0;
        transform: scale(0.6);
        pointer-events: none;
    }

    .fft-canvas {
        width: 100%;
        height: 100%;
    }

    .drop-hint {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        box-sizing: border-box;
        border: dashed 10px #227fff;
    }

    .drop-zone {
        background-color: #227fff99;
        border: solid 10px #227fff;
    }
</style>
